import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { flyToCart } from "../lib/navHelpers";
import axios from "axios";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Minus, ShoppingBag, AlertTriangle, ChevronDown } from "lucide-react";
import { API, useLang, useCart, useSettings, fmtDzd, assetUrl, cldUrl, cldSrcSet } from "../lib/providers";
import ReassuranceBlock from "./ReassuranceBlock";
import Trust from "./Trust";
import ProductCard from "./ProductCard";
import { firePixelEvent } from "./TrackingScripts";
import { isSoldOut } from "./ProductCard";

const STOCK_LABELS = {
  available: { fr: "En stock", ar: "متوفر", color: "text-[#22C55E]" },
  on_order: { fr: "Sur commande", ar: "حسب الطلب", color: "text-neon" },
  out_of_stock: { fr: "RUPTURE DE STOCK", ar: "نفذ المخزون", color: "text-red-500 font-black text-base" },
};

// Purely visual FR/EN color-name → CSS color mapping for the swatch selector below.
// No business data — used only to render a swatch when the existing variant value
// is a recognizable color word. Unknown values simply fall back to a text pill.
const COLOR_SWATCH_MAP = {
  noir: "#111111", black: "#111111",
  blanc: "#f5f5f5", white: "#f5f5f5",
  gris: "#9ca3af", grey: "#9ca3af", gray: "#9ca3af",
  rouge: "#dc2626", red: "#dc2626",
  bleu: "#2563eb", blue: "#2563eb", "bleu marine": "#1e3a8a", navy: "#1e3a8a",
  vert: "#16a34a", green: "#16a34a",
  jaune: "#eab308", yellow: "#eab308",
  orange: "#f97316",
  rose: "#ec4899", pink: "#ec4899",
  violet: "#8b5cf6", purple: "#8b5cf6",
  marron: "#78350f", brown: "#78350f",
  beige: "#e5d3b3",
  doré: "#c9a227", dore: "#c9a227", gold: "#c9a227",
  argenté: "#c0c0c0", argente: "#c0c0c0", silver: "#c0c0c0",
  turquoise: "#14b8a6",
  bordeaux: "#7f1d1d", maroon: "#7f1d1d",
  kaki: "#6b7043", khaki: "#6b7043",
};
const colorSwatchHex = (value) => {
  const v = (value || "").toLowerCase().trim();
  if (COLOR_SWATCH_MAP[v]) return COLOR_SWATCH_MAP[v];
  for (const part of v.split(/\s*[-/–|]\s*/)) if (COLOR_SWATCH_MAP[part.trim()]) return COLOR_SWATCH_MAP[part.trim()];
  return null;
};
// Nom affiché sous les pastilles : la partie française (« Black - Noir » → « Noir »)
const colorShown = (value, lang) => (lang === "ar" ? value : String(value || "").split(/\s+-\s+/).pop());
const isColorOption = (opt) => /couleur|color/i.test(`${opt?.id || ""} ${opt?.label_fr || ""} ${opt?.label_ar || ""}`);

const SPEC_LABELS_FR = {
  weight_g: "Poids",
  head_size_in2: "Tamis",
  string_pattern: "Plan de cordage",
  balance_cm: "Équilibre",
  stiffness_ra: "Rigidité (RA)",
  swing_weight: "Swing weight",
  length_cm: "Longueur",
  gauge_mm: "Jauge",
  length_m: "Longueur cordage",
  shoe_surface: "Surface",
  level: "Niveau",
};
const SPEC_UNITS = { weight_g: " g", head_size_in2: " in²", balance_cm: " cm", length_cm: " cm", gauge_mm: " mm", length_m: " m" };
const SPEC_LABELS_AR = {
  weight_g: "الوزن", head_size_in2: "حجم الرأس", string_pattern: "نمط الأوتار",
  balance_cm: "التوازن", level: "المستوى", length_cm: "الطول", gauge_mm: "السماكة",
  length_m: "طول الوتر", shoe_surface: "السطح",
};


function ImageGallery({ images, name, isNew, lang }) {
  const [active, setActive] = useState(0);
  const photos = images && images.length > 0 ? images : [];
  if (photos.length === 0) {
    return <div className="aspect-square bg-black/30 flex items-center justify-center text-white/30 text-sm">No image</div>;
  }
  return (
    <div>
      <div className="aspect-square bg-black/30 overflow-hidden mb-2 relative">
        {isNew && (
          <div className="absolute top-3 start-3 px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-neon text-black border border-black/10 shadow-lg z-10" data-testid="detail-new-badge">
            {lang === "ar" ? "جديد" : "Nouveau"}
          </div>
        )}
        <img
          src={cldUrl(photos[active], { w: 800 })}
          srcSet={cldSrcSet(photos[active], [480, 800, 1200])}
          sizes="(max-width: 640px) 100vw, 50vw"
          alt={name}
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          data-testid="detail-main-image"
        />
      </div>
      {photos.length > 1 && (
        <div className="flex gap-2 overflow-x-auto">
          {photos.map((p, i) => (
            <button key={i} onClick={() => setActive(i)}
              className={`shrink-0 w-16 h-16 border-2 ${active === i ? "border-neon" : "border-white/10"}`}
              data-testid={`detail-thumb-${i}`}>
              <img src={cldUrl(p, { w: 160 })} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


export default function ProductDetailSheet({ product, open, onOpenChange }) {
  const navigate = useNavigate();
  const { lang } = useLang();
  const { add, setOpen: setCartOpen } = useCart();
  const { settings } = useSettings();
  const [selectedOpts, setSelectedOpts] = useState({});
  const [qty, setQty] = useState(1);
  const [crossSell, setCrossSell] = useState([]);
  // When details collapsing is enabled (CMS), this controls whether the long content is shown
  const detailsCollapsible = settings?.product_details_collapsible !== false;
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Re-collapse details whenever the product changes
  useEffect(() => { setDetailsOpen(false); }, [product?.id]);

  // Reset state when product changes (do NOT auto-pick first value — force user to choose)
  useEffect(() => {
    setSelectedOpts({});
    setQty(1);
  }, [product?.id]); // eslint-disable-line

  // Cross-sell: load related products in suggested categories
  useEffect(() => {
    if (!product || !open) return;
    const rules = settings.cross_sell_rules || [];
    const matching = rules.filter((r) => r.trigger_category === product.category && r.enabled !== false);
    if (matching.length === 0) { setCrossSell([]); return; }
    const cats = [...new Set(matching.map((r) => r.suggest_category))];
    Promise.all(cats.map((c) => axios.get(`${API}/products?category=${c}&limit=4`).then((r) => r.data).catch(() => [])))
      .then((arr) => {
        const flat = [].concat(...arr).filter((p) => p.id !== product.id).slice(0, 4);
        setCrossSell(flat);
      });
  }, [product?.id, open, settings.cross_sell_rules]); // eslint-disable-line

  // Find matching variant for current selection (must be called before any conditional return — Rules of Hooks)
  const matchedVariant = useMemo(() => {
    if (!product || !product.has_variants || !product.variants?.length) return null;
    return product.variants.find((v) => {
      return Object.keys(selectedOpts).every((k) => v.options?.[k] === selectedOpts[k]);
    });
  }, [product, selectedOpts]);

  // Fire ViewContent on product view (must be BEFORE any early return to respect Rules of Hooks)
  useEffect(() => {
    if (!product || !open) return;
    const priceEur = product.variants?.[0]?.price_eur ?? product.price_eur ?? 0;
    firePixelEvent("ViewContent", {
      value: priceEur,
      currency: "EUR",
      content_ids: [product.id],
      content_name: product.name,
      content_type: "product",
      content_category: product.category,
    });
  }, [product?.id, open]); // eslint-disable-line

  if (!product) return null;

  const finalPrice = product.price_eur + (matchedVariant?.price_delta_eur || 0);
  const dzd = product.price_dzd > 0 && finalPrice === product.price_eur ? product.price_dzd : finalPrice * (settings.exchange_rate || 280);
  const compareAt = product.compare_at_dzd > 0 ? product.compare_at_dzd : 0;
  const discountPct = compareAt > dzd ? Math.round(((compareAt - dzd) / compareAt) * 100) : 0;
  const stock = STOCK_LABELS[isSoldOut(product) ? "out_of_stock" : product.stock_status] || STOCK_LABELS.available;
  const stockCount = matchedVariant ? (matchedVariant.stock_count || 0) : (product.stock_count || 0);

  const t = (fr, ar) => (lang === "ar" ? ar : fr);
  const desc = lang === "ar" && product.description_ar ? product.description_ar : product.description;
  const specs = product.ai_specs || {};
  const specEntries = Object.entries(specs).filter(([, v]) => v !== null && v !== undefined && v !== "");

  const validateAndPayload = () => {
    if (product.has_variants) {
      const missing = (product.options || []).filter((o) => (o.values?.length || 0) > 0 && !selectedOpts[o.id]);
      if (missing.length) {
        const labels = missing.map((o) => (lang === "ar" ? (o.label_ar || o.label_fr) : o.label_fr)).join(", ");
        const msg = lang === "ar"
          ? `الرجاء اختيار : ${labels} قبل الإضافة إلى السلة`
          : `Veuillez choisir : ${labels} avant de continuer.`;
        toast.error(msg, { duration: 4000 });
        // Ramène le client sur les pastilles de couleur / taille à choisir
        document.querySelector('[data-testid^="detail-variant-"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
        return null;
      }
      // If a variant matrix exists, ensure the picked combo is in stock
      if (product.variants?.length && matchedVariant && (matchedVariant.stock_count === 0)) {
        toast.error(lang === "ar" ? "هذه المتغيرة نفذت" : "Cette combinaison est en rupture.");
        return null;
      }
    }
    return {
      ...product,
      _variant: matchedVariant,
      _selected_options: { ...selectedOpts },
      // Stock limits propagated to the cart so qty can never exceed them
      variant_stock_count: matchedVariant?.stock_count ?? null,
    };
  };

  // Fire ViewContent on product view
  const handleAddToCart = (e) => {
    const payload = validateAndPayload();
    if (!payload) return;
    add(payload, qty, false); // ajout silencieux
    // Animation : la photo du produit vole vers le bouton panier du header
    const sheetImg = document.querySelector('[data-testid="product-detail-sheet"] img');
    flyToCart(e && e.currentTarget, sheetImg && sheetImg.src);
    firePixelEvent("AddToCart", {
      value: (payload.price_eur || 0) * qty,
      currency: "EUR",
      content_ids: [product.id],
      content_name: product.name,
      content_type: "product",
      quantity: qty,
    });
    toast.success(lang === "ar" ? "تمت الإضافة إلى السلة ✓ — تابع التسوق" : "Ajouté au panier ✓ — continuez vos achats");
    if (product.has_variants) {
      // Reste sur la fiche produit : permet de changer de variante (ex: couleur)
      // et de l'ajouter à nouveau sans quitter la page. Le sélecteur reste actif.
      setQty(1);
    } else {
      onOpenChange(false);
    }
  };

  const handleBuyNow = () => {
    const payload = validateAndPayload();
    if (!payload) return;
    add(payload, qty, false); // ajoute sans ouvrir le drawer
    firePixelEvent("AddToCart", {
      value: (payload.price_eur || 0) * qty,
      currency: "EUR",
      content_ids: [product.id],
      content_name: product.name,
      content_type: "product",
      quantity: qty,
    });
    onOpenChange(false);
    // Redirection vers la page panier pleine (utilisateur complète l'adresse + paiement)
    setTimeout(() => navigate("/panier"), 100);
  };

  // Are all required options selected? (used for visual cue)
  const allOptionsFilled = !product.has_variants || (product.options || []).every((o) => !(o.values?.length) || selectedOpts[o.id]);
  // Disable buttons only when truly impossible (out of stock entirely)
  const isHardDisabled = isSoldOut(product);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-[var(--c-bg)] border-white/10 text-white w-full sm:max-w-2xl overflow-y-auto p-0"
        data-testid="product-detail-sheet"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-6 sm:p-8">
          <SheetHeader className="mb-4">
            <Badge className="bg-white/10 text-white rounded-none w-fit text-[10px] mb-2">{product.brand}</Badge>
            <SheetTitle className="font-heading font-black uppercase tracking-tight text-2xl sm:text-3xl text-white" data-testid="detail-product-name">
              {product.name}
            </SheetTitle>
            <SheetDescription className="sr-only">
              {product.name} — {product.brand}
            </SheetDescription>
          </SheetHeader>
          {product.tags_fr && product.tags_fr.length > 0 && (
            <div className="flex gap-1 flex-wrap mb-4 -mt-2" data-testid="detail-tags">
              {product.tags_fr.slice(0, 5).map((t, i) => (<Badge key={i} className="bg-neon/10 text-neon border-neon/30 rounded-none text-[10px]">{t}</Badge>))}
            </div>
          )}

          {/* Image gallery — swap to variant image when a matching variant with image_url is selected */}
          {(() => {
            const variantImg = matchedVariant?.image_url || "";
            const baseImages = (product.images && product.images.length > 0) ? product.images : (product.image_url ? [product.image_url] : []);
            const galleryImages = variantImg
              ? [variantImg, ...baseImages.filter((u) => u !== variantImg)]
              : baseImages;
            return <ImageGallery key={variantImg || "base"} images={galleryImages} name={product.name} isNew={!!product.is_new} lang={lang} />;
          })()}

          {/* Price + stock */}
          <div className="flex items-end justify-between mt-6 mb-6">
            <div>
              {compareAt > 0 && discountPct > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-base text-white/40 line-through font-medium" data-testid="detail-compare-at">{fmtDzd(compareAt)}</span>
                  <span className="text-xs font-black uppercase tracking-widest bg-[#EF4444] text-white px-2 py-1" data-testid="detail-discount-pct">-{discountPct}%</span>
                </div>
              )}
              <div className="font-heading font-black text-3xl text-neon" data-testid="detail-price-dzd">{fmtDzd(dzd)}</div>
            </div>
            <div className={`text-xs font-bold uppercase tracking-widest ${stock.color}`} data-testid="detail-stock">
              {t(stock.fr, stock.ar)}
              {stockCount > 0 && stockCount <= 5 && <span className="ms-2">· {stockCount}</span>}
            </div>
          </div>

          {/* Offre « cordage offert » (seuil = palier cadeau le plus élevé) */}
          {(() => {
            if (settings?.gift_offer_enabled === false) return null;
            const top = (settings?.gift_tiers || [])
              .filter((x) => x?.enabled !== false && x?.product_id && Number(x?.threshold_dzd) > 0)
              .sort((a, b) => Number(b.threshold_dzd) - Number(a.threshold_dzd))[0];
            const raw = lang === "ar" ? settings?.gift_offer_text_ar : settings?.gift_offer_text_fr;
            if (!top || !raw) return null;
            return (
              <div className="mb-5 -mt-2 border border-neon/30 bg-neon/5 px-3 py-2.5 text-sm" data-testid="detail-gift-offer">
                {raw.split("{threshold}").join(fmtDzd(top.threshold_dzd))}
              </div>
            );
          })()}

          {/* Variants */}
          {product.has_variants && product.options && product.options.length > 0 && (
            <div className="space-y-3 mb-6">
              {(() => {
                // For each option, compute the available values based on OTHER selected options
                // (so e.g. picking Blanc only shows gauges that have Blanc in stock).
                const computeAvailable = (optId) => {
                  const otherSel = Object.fromEntries(
                    Object.entries(selectedOpts).filter(([k, v]) => k !== optId && v)
                  );
                  const result = {}; // {value: total_stock_count}
                  for (const v of (product.variants || [])) {
                    const vo = v.options || {};
                    // Variant must match every OTHER selected option
                    const matches = Object.entries(otherSel).every(([k, val]) => vo[k] === val);
                    if (!matches) continue;
                    const myVal = vo[optId];
                    if (myVal === undefined || myVal === null) continue;
                    const sc = (v.stock_count == null) ? null : Number(v.stock_count);
                    if (result[myVal] == null) result[myVal] = sc;
                    else if (sc != null) result[myVal] = (result[myVal] || 0) + sc;
                  }
                  return result;
                };

                return product.options.map((opt) => {
                  const avail = computeAvailable(opt.id);
                  const pickOptionValue = (v) => {
                    setSelectedOpts((s) => {
                      const next = { ...s, [opt.id]: v };
                      // If after this change, a different selected option becomes
                      // unavailable for the new combo, clear it.
                      for (const otherOpt of (product.options || [])) {
                        if (otherOpt.id === opt.id) continue;
                        const cur = next[otherOpt.id];
                        if (!cur) continue;
                        const otherSel = Object.fromEntries(Object.entries(next).filter(([k, vv]) => k !== otherOpt.id && vv));
                        const anyMatch = (product.variants || []).some((vv) => {
                          const vo = vv.options || {};
                          if (vo[otherOpt.id] !== cur) return false;
                          return Object.entries(otherSel).every(([k, val]) => vo[k] === val);
                        });
                        if (!anyMatch) delete next[otherOpt.id];
                      }
                      return next;
                    });
                  };
                  return (
                    <div key={opt.id}>
                      <label className="text-[10px] uppercase tracking-widest text-white/60 mb-1 block">
                        {lang === "ar" ? (opt.label_ar || opt.label_fr) : opt.label_fr}
                      </label>
                      {isColorOption(opt) ? (
                        <div className="flex flex-wrap gap-2.5" data-testid={`detail-variant-${opt.id}`}>
                          {(opt.values || []).map((v) => {
                            const stock = avail[v];
                            const isAvailable = stock === null || (stock != null && stock > 0);
                            const hex = colorSwatchHex(v);
                            const selected = selectedOpts[opt.id] === v;
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => isAvailable && pickOptionValue(v)}
                                disabled={!isAvailable}
                                title={`${colorShown(v, lang)}${stock != null ? (isAvailable ? ` — ${stock} ${t("dispo", "متوفر")}` : ` — ${t("rupture", "نفذ")}`) : ""}`}
                                data-testid={`detail-variant-${opt.id}-${v}`}
                                className={`relative w-11 h-11 rounded-full border-2 flex items-center justify-center transition-all ${
                                  selected ? "border-neon scale-110" : "border-white/25 hover:border-white/60"
                                } ${!isAvailable ? "opacity-40 grayscale cursor-not-allowed" : "cursor-pointer"}`}
                                style={hex ? { backgroundColor: hex } : { backgroundColor: "rgba(255,255,255,0.08)" }}
                              >
                                {!hex && (
                                  <span className="text-[9px] font-bold uppercase text-white px-0.5 leading-none">
                                    {colorShown(v, lang).slice(0, 3)}
                                  </span>
                                )}
                                {!isAvailable && <span aria-hidden className="absolute left-1 right-1 top-1/2 h-0.5 bg-white/90 -rotate-45" />}
                              </button>
                            );
                          })}
                          {selectedOpts[opt.id] && (
                            <span className="w-full text-xs text-white/70 mt-0.5">{colorShown(selectedOpts[opt.id], lang)}</span>
                          )}
                        </div>
                      ) : (
                        <Select value={selectedOpts[opt.id] || ""} onValueChange={pickOptionValue}>
                          <SelectTrigger className="bg-black/40 border-white/10 rounded-none" data-testid={`detail-variant-${opt.id}`}>
                            <SelectValue placeholder={t("Choisir", "اختر")} />
                          </SelectTrigger>
                          <SelectContent className="bg-[var(--c-bg)] border-white/10 text-white">
                            {(opt.values || []).map((v) => {
                              const stock = avail[v];
                              const isAvailable = stock === null || (stock != null && stock > 0);
                              return (
                                <SelectItem
                                  key={v}
                                  value={v}
                                  disabled={!isAvailable}
                                  data-testid={`detail-variant-${opt.id}-${v}`}
                                >
                                  <span className={!isAvailable ? "opacity-40 line-through" : ""}>
                                    {v}
                                    {stock != null && (
                                      <span className={`ms-2 text-[11px] ${isAvailable ? "text-neon/80" : "text-white/40"}`}>
                                        ({stock > 0 ? `${stock} ${t("dispo", "متوفر")}` : t("rupture", "نفذ")})
                                      </span>
                                    )}
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  );
                });
              })()}
              {matchedVariant && (
                <div className="text-xs">
                  {matchedVariant.stock_count === 0 ? (
                    <p className="text-clay">{t("Cette variante est en rupture", "هذه المتغيرة نفذت")}</p>
                  ) : matchedVariant.stock_count != null ? (
                    <p className="text-neon font-bold">
                      ✓ {matchedVariant.stock_count} {t("en stock pour cette combinaison", "متوفر لهذا التشكيل")}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {/* === Summary (short pitch) — always visible === */}
          {(() => {
            const summary = lang === "ar"
              ? (product.summary_ar || product.summary_fr || "")
              : (product.summary_fr || product.summary_ar || "");
            if (!summary.trim()) return null;
            return (
              <div
                className="mb-5 p-4 border-l-2 border-neon bg-neon/5"
                dir={lang === "ar" ? "rtl" : "ltr"}
                data-testid="detail-summary"
              >
                <p className="text-sm sm:text-[15px] text-white/90 leading-relaxed font-medium whitespace-pre-line">
                  {summary}
                </p>
              </div>
            );
          })()}

          {/* === Long content (Description + Specs) — collapsible when enabled via CMS === */}
          {(() => {
            const hasDesc = !!desc;
            const hasFeatures = product.key_features_fr && product.key_features_fr.length > 0;
            const hasSpecs = specEntries.length > 0;
            const cmsSpecsList = [
              { k: "spec_weight", fr: "Poids", ar: "الوزن" },
              { k: "spec_balance", fr: "Équilibre", ar: "التوازن" },
              { k: "spec_head_size", fr: "Taille tamis", ar: "حجم الرأس" },
              { k: "spec_string_pattern", fr: "Plan cordage", ar: "نمط الأوتار" },
              { k: "spec_format", fr: "Format", ar: "التنسيق" },
              { k: "spec_size", fr: "Taille / Pointure", ar: "المقاس" },
            ].filter((s) => (product[s.k] || "").trim());
            const customSpecs = (settings.custom_filters || [])
              .filter((f) => f.id && f.label_fr)
              .map((f) => ({ k: `custom:${f.id}`, fr: f.label_fr, ar: f.label_ar || f.label_fr, value: ((product.custom_specs || {})[f.id] || "").trim() }))
              .filter((s) => s.value);
            const hasCmsSpecs = cmsSpecsList.length > 0 || customSpecs.length > 0;
            const hasAnyContent = hasDesc || hasFeatures || hasSpecs || hasCmsSpecs;
            if (!hasAnyContent) return null;

            const body = (
              <>
                {hasDesc && (
                  <div className="mb-6" dir={lang === "ar" ? "rtl" : "ltr"}>
                    <h4 className="text-[10px] uppercase tracking-widest text-white/60 mb-2">{t("Description", "الوصف")}</h4>
                    <p className="text-sm text-white/85 leading-relaxed whitespace-pre-line" data-testid="detail-description">{desc}</p>
                  </div>
                )}
                {hasFeatures && (
                  <div className="mb-6">
                    <h4 className="text-[10px] uppercase tracking-widest text-white/60 mb-2">{t("Points forts", "النقاط القوية")}</h4>
                    <ul className="space-y-1 text-sm">
                      {product.key_features_fr.map((f, i) => (
                        <li key={i} className="flex gap-2 items-start"><span className="text-neon shrink-0">•</span>{f}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {hasSpecs && (
                  <div className="mb-6">
                    <h4 className="text-[10px] uppercase tracking-widest text-white/60 mb-2">{t("Caractéristiques", "المواصفات")}</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="detail-specs">
                      {specEntries.map(([k, v]) => (
                        <React.Fragment key={k}>
                          <dt className="text-white/50">{lang === "ar" ? (SPEC_LABELS_AR[k] || k) : (SPEC_LABELS_FR[k] || k)}</dt>
                          <dd className="text-white font-bold">{v}{SPEC_UNITS[k] || ""}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  </div>
                )}
                {hasCmsSpecs && (
                  <div className="mb-6">
                    <h4 className="text-[10px] uppercase tracking-widest text-white/60 mb-2">{t("Spécifications", "المواصفات التقنية")}</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="detail-cms-specs">
                      {cmsSpecsList.map((s) => (
                        <React.Fragment key={s.k}>
                          <dt className="text-white/50">{lang === "ar" ? s.ar : s.fr}</dt>
                          <dd className="text-white font-bold" data-testid={`detail-${s.k}`}>{product[s.k]}</dd>
                        </React.Fragment>
                      ))}
                      {customSpecs.map((s) => (
                        <React.Fragment key={s.k}>
                          <dt className="text-white/50">{lang === "ar" ? s.ar : s.fr}</dt>
                          <dd className="text-white font-bold" data-testid={`detail-${s.k}`}>{s.value}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  </div>
                )}
              </>
            );

            if (!detailsCollapsible) return <div data-testid="detail-long-content">{body}</div>;

            return (
              <div className="mb-2" data-testid="detail-collapsible">
                <button
                  type="button"
                  onClick={() => setDetailsOpen((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 py-3 border-y border-white/10 hover:bg-white/[0.03] transition-colors group"
                  aria-expanded={detailsOpen}
                  data-testid="detail-collapsible-toggle"
                >
                  <span className="text-xs uppercase tracking-widest font-black text-white group-hover:text-neon transition-colors">
                    {detailsOpen
                      ? t("Masquer les détails", "إخفاء التفاصيل")
                      : t("Voir tous les détails", "عرض جميع التفاصيل")}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-white/60 group-hover:text-neon transition-transform ${detailsOpen ? "rotate-180" : ""}`} />
                </button>
                {detailsOpen && (
                  <div className="pt-4 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="detail-collapsible-body">
                    {body}
                  </div>
                )}
              </div>
            );
          })()}


          {/* Quantity + dual action buttons */}
          <div className="space-y-2 mb-6 sticky bottom-0 bg-[var(--c-bg)] py-3 border-t border-white/10 -mx-6 sm:-mx-8 px-6 sm:px-8">
            {!allOptionsFilled && !isHardDisabled && (
              <div className="flex items-center gap-2 text-[11px] text-clay border border-clay/30 bg-clay/10 px-3 py-2" data-testid="variant-warning">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                <span>{t("Choisissez toutes les options ci-dessus avant de commander.", "اختر جميع الخيارات أعلاه قبل الطلب.")}</span>
              </div>
            )}
            <div className="flex items-center gap-3">
              {(() => {
                const maxStock = matchedVariant?.stock_count ?? product.stock_count ?? null;
                const atMax = maxStock != null && qty >= maxStock;
                return (
                  <div className="flex items-center border border-white/20">
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-11 hover:bg-white/10" data-testid="detail-qty-minus"><Minus className="w-4 h-4 mx-auto" /></button>
                    <span className="px-4 font-heading font-black text-lg" data-testid="detail-qty">{qty}</span>
                    <button
                      onClick={() => {
                        if (atMax) { toast.error(`Stock max atteint (${maxStock} dispo)`); return; }
                        setQty((q) => (maxStock != null ? Math.min(maxStock, q + 1) : q + 1));
                      }}
                      disabled={atMax}
                      className={`w-9 h-11 ${atMax ? "text-white/30 cursor-not-allowed" : "hover:bg-white/10"}`}
                      data-testid="detail-qty-plus"
                      title={atMax ? `Stock max : ${maxStock}` : "Augmenter"}
                    ><Plus className="w-4 h-4 mx-auto" /></button>
                  </div>
                );
              })()}
              <Button
                onClick={handleAddToCart}
                disabled={isHardDisabled}
                variant="ghost"
                className={`flex-1 rounded-none font-black uppercase tracking-widest h-11 text-xs border bg-white/5 hover:bg-white/15 text-white border-white/15 disabled:opacity-40`}
                data-testid="detail-add-to-cart"
              >
                <ShoppingBag className="w-4 h-4 me-2" />
                {t("Ajouter au panier", "أضف إلى السلة")}
              </Button>
            </div>
            <Button
              onClick={handleBuyNow}
              disabled={isHardDisabled}
              className={`w-full rounded-none font-black uppercase tracking-widest h-12 text-sm bg-neon text-black hover:opacity-90 disabled:opacity-40`}
              data-testid="detail-buy-now"
            >
              ⚡ {t("Achat direct → Panier", "شراء مباشر → السلة")}
            </Button>
          </div>

          {/* Trust lines (cartes confiance — variante lignes) */}
          <div className="border-t border-white/10 pt-6 mt-2 -mx-6 sm:-mx-8">
            <Trust variant="lines" />
          </div>

          {/* Reassurance block (configurable in CMS — fallback if Trust is disabled) */}
          <div className="-mx-6 sm:-mx-8">
            <ReassuranceBlock variant="lines" compact />
          </div>

          {/* Cross-sell */}
          {crossSell.length > 0 && (
            <div className="border-t border-white/10 pt-6 mt-2">
              <h4 className="font-heading font-black uppercase tracking-tight text-base mb-3">
                {t("Idéal avec ce produit", "مثالي مع هذا المنتج")}
              </h4>
              <div className="grid grid-cols-2 gap-3" data-testid="cross-sell-grid">
                {crossSell.map((p) => (<ProductCard key={p.id} product={p} />))}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
